import { useTranslation } from 'react-i18next';
import { IpponButton, IpponHSpace } from '@ippon-ui/react';
import type { Result } from '@/generic/functional';
import { LoaderComponent, useLoader } from '@/generic/loader';
import {
  initAccount,
  RULE_IMPORT,
  type User,
  type WithoutOrganization,
} from '@/supporting/account';
import { RulesImportComponent } from '@/rules-management/primary/RulesImportComponent.tsx';
import { DraftRulesTriggerComponent } from '@/rules-management/primary/DraftRulesTriggerComponent.tsx';
import { useRuleExtractionTracking } from '@/rules-management/primary/RuleExtractionTrackingContext.ts';

const canImportRules = (user: User): boolean =>
  user.organization.map((organization) => organization.authorities.has(RULE_IMPORT)).orElse(false);

const ImportComponent = () => {
  const { t } = useTranslation('rules-management');
  const tracking = useRuleExtractionTracking();

  return (
    <>
      <IpponButton
        onClick={tracking.openImport}
        iconRight={{ name: 'add' }}
        dataSelector="rules-management.rules.import-trigger"
      >
        {t('rules-management.rules.import-trigger')}
      </IpponButton>
      <RulesImportComponent />
    </>
  );
};

const AuthorizedRulesActions = () => (
  <IpponHSpace gap={8} align="middle">
    <DraftRulesTriggerComponent />
    <ImportComponent />
  </IpponHSpace>
);

const withoutImport = () => null;

const onUser = (user: User) => (canImportRules(user) ? <AuthorizedRulesActions /> : null);

const onCurrentUser = (currentUser: Result<WithoutOrganization, User>) =>
  currentUser.fold({
    error: withoutImport,
    success: onUser,
  });

export const RulesImportTriggerComponent = () => {
  const { injectUsersRepository } = initAccount();

  const userLoader = useLoader(() => injectUsersRepository().currentUser(), []);

  return (
    <LoaderComponent
      loader={userLoader}
      onLoading={withoutImport}
      onError={withoutImport}
      onSuccess={onCurrentUser}
    />
  );
};
